import type { PersonalSettingsSection, SettingsSection, View } from "./types";

export type StatusTaskRoute = { columnId: string; taskId: string };

function statusDetailRoute(parts: string[]): { workId?: string; intake?: StatusTaskRoute } {
  if (parts[0] === "works" && parts[1]) return { workId: parts[1] };
  if (parts[0] === "columns" && parts[1] && parts[2] === "tasks" && parts[3]) return { intake: { columnId: parts[1], taskId: parts[3] } };
  return {};
}

function statusDetailPath(route: { workId?: string; intake?: StatusTaskRoute }): string {
  if (route.workId) return `/works/${encodeURIComponent(route.workId)}`;
  return route.intake ? `/columns/${encodeURIComponent(route.intake.columnId)}/tasks/${encodeURIComponent(route.intake.taskId)}` : "";
}

export type WorkspaceRoute =
  | { view: "dashboard" }
  | { view: "calendar" }
  | { view: "org-chart"; page?: "rules" }
  | { view: "company-rule-management" }
  | { view: "work-guides" }
  | { view: "work-guide-management" }
  | { view: "boards" }
  | { view: "status"; dashboardId?: string; workId?: string; intake?: StatusTaskRoute }
  | { view: "tasks"; taskId?: string; page?: "completed" }
  | { view: "collaboration" }
  | { view: "projects"; projectId?: string; page?: "overview" | "board" | "budget" | "document-new" | "document-edit"; documentId?: string; workId?: string; intake?: StatusTaskRoute }
  | { view: "performance" }
  | { view: "evaluation" }
  | { view: "notes"; noteId?: string }
  | { view: "task-forms" }
  | { view: "settings"; section: SettingsSection }
  | { view: "account-management" }
  | { view: "announcement-management" }
  | { view: "my-work" }
  | { view: "leave"; section?: "approvals" }
  | { view: "attendance-management"; requestId?: string }
  | { view: "leave-management" }
  | { view: "personal-settings"; section: PersonalSettingsSection };

const viewRoutes: Record<Exclude<View, "settings">, WorkspaceRoute> = {
  dashboard: { view: "dashboard" },
  calendar: { view: "calendar" },
  "org-chart": { view: "org-chart" },
  "company-rule-management": { view: "company-rule-management" },
  "work-guides": { view: "work-guides" },
  "work-guide-management": { view: "work-guide-management" },
  boards: { view: "boards" },
  status: { view: "status" },
  tasks: { view: "tasks" },
  collaboration: { view: "collaboration" },
  projects: { view: "projects" },
  performance: { view: "performance" },
  evaluation: { view: "evaluation" },
  notes: { view: "notes" },
  "task-forms": { view: "task-forms" },
  "account-management": { view: "account-management" },
  "announcement-management": { view: "announcement-management" },
  "my-work": { view: "my-work" },
  leave: { view: "leave" },
  "attendance-management": { view: "attendance-management" },
  "leave-management": { view: "leave-management" },
  "personal-settings": { view: "personal-settings", section: "general" },
};

const settingsSections = new Set<SettingsSection>(["system", "org", "permissions", "attendance", "work", "evaluation", "logs"]);

function segment(value: string): string {
  try { return decodeURIComponent(value); }
  catch { return value; }
}

export function isPlatformLocation(location: Pick<Location, "pathname" | "hash"> = window.location): boolean {
  return location.pathname.replace(/\/+$/, "") === "/platform" || location.hash === "#platform";
}

export function routeForView(view: View): WorkspaceRoute {
  return view === "settings" ? { view, section: "system" } : viewRoutes[view];
}

export function parseWorkspaceRoute(location: Pick<Location, "pathname" | "search"> = window.location): WorkspaceRoute {
  const parts = location.pathname.split("/").filter(Boolean).map(segment);
  if (!parts.length || parts[0] === "dashboard") return { view: "dashboard" };
  if (parts[0] === "boards") return { view: "boards" };
  if (parts[0] === "calendar") return { view: "calendar" };
  if (parts[0] === "status") return { view: "status", ...(parts[1] ? { dashboardId: parts[1] } : {}), ...statusDetailRoute(parts.slice(2)) };
  if (parts[0] === "tasks" && parts[1] === "completed") return { view: "tasks", page: "completed", taskId: parts[2] };
  if (parts[0] === "tasks") return { view: "tasks", taskId: parts[1] };
  if (parts[0] === "collaboration") return { view: "collaboration" };
  if (parts[0] === "projects") {
    if (parts[1] === "budget") return { view: "projects", page: "budget" };
    if (parts[1] && parts[2] === "board") return { view: "projects", projectId: parts[1], page: "board", ...statusDetailRoute(parts.slice(3)) };
    if (parts[1] && parts[2] === "documents" && parts[3] === "new") return { view: "projects", projectId: parts[1], page: "document-new" };
    if (parts[1] && parts[2] === "documents" && parts[3] && parts[4] === "edit") {
      return { view: "projects", projectId: parts[1], page: "document-edit", documentId: parts[3] };
    }
    return { view: "projects", projectId: parts[1] };
  }
  if (parts[0] === "performance") return { view: "performance" };
  if (parts[0] === "evaluation") return { view: "evaluation" };
  if (parts[0] === "research-notes") return { view: "notes", noteId: parts[1] };
  if (parts[0] === "task-forms") return { view: "task-forms" };
  if (parts[0] === "settings") {
    const section = parts[1] as SettingsSection | undefined;
    return { view: "settings", section: section && settingsSections.has(section) ? section : "system" };
  }
  if (parts[0] === "accounts" && parts[1] === "manage") return { view: "account-management" };
  if (parts[0] === "announcements" && parts[1] === "manage") return { view: "announcement-management" };
  if (parts[0] === "company-rules" && parts[1] === "manage") return { view: "company-rule-management" };
  if (parts[0] === "work-guides") return { view: parts[1] === "manage" ? "work-guide-management" : "work-guides" };
  if (parts[0] === "people" && parts[1] === "work") return { view: "my-work" };
  if (parts[0] === "people" && parts[1] === "organization") return { view: "org-chart", ...(parts[2] === "rules" ? { page: "rules" as const } : {}) };
  if (parts[0] === "people" && parts[1] === "leave") return { view: "leave", ...(parts[2] === "approvals" ? { section: "approvals" as const } : {}) };
  if (parts[0] === "attendance" && parts[1] === "manage") return parts[2]
    ? { view: "attendance-management", requestId: parts[2] } : { view: "attendance-management" };
  if (parts[0] === "leave" && parts[1] === "manage") return { view: "leave-management" };
  if (parts[0] === "personal-settings") {
    const section = parts[1];
    return { view: "personal-settings", section: section === "notifications" || section === "theme" || section === "password" ? section : "general" };
  }
  if (parts[0] === "notifications" && parts[1] === "settings") return { view: "personal-settings", section: "notifications" };
  return { view: "dashboard" };
}

export function workspaceRoutePath(route: WorkspaceRoute): string {
  switch (route.view) {
    case "dashboard": return "/";
    case "calendar": return "/calendar";
    case "org-chart": return route.page === "rules" ? "/people/organization/rules" : "/people/organization";
    case "company-rule-management": return "/company-rules/manage";
    case "work-guides": return "/work-guides";
    case "work-guide-management": return "/work-guides/manage";
    case "boards": return "/boards";
    case "status": return route.dashboardId ? `/status/${encodeURIComponent(route.dashboardId)}${statusDetailPath(route)}` : "/status";
    case "tasks": {
      if (route.page === "completed") return route.taskId
        ? `/tasks/completed/${encodeURIComponent(route.taskId)}`
        : "/tasks/completed";
      return route.taskId ? `/tasks/${encodeURIComponent(route.taskId)}` : "/tasks";
    }
    case "collaboration": return "/collaboration";
    case "projects": {
      if (route.page === "budget") return "/projects/budget";
      if (!route.projectId) return "/projects";
      const base = `/projects/${encodeURIComponent(route.projectId)}`;
      if (route.page === "board") return `${base}/board${statusDetailPath(route)}`;
      if (route.page === "document-new") return `${base}/documents/new`;
      if (route.page === "document-edit" && route.documentId) return `${base}/documents/${encodeURIComponent(route.documentId)}/edit`;
      return base;
    }
    case "performance": return "/performance";
    case "evaluation": return "/evaluation";
    case "notes": return route.noteId ? `/research-notes/${encodeURIComponent(route.noteId)}` : "/research-notes";
    case "task-forms": return "/task-forms";
    case "settings": return `/settings/${route.section}`;
    case "account-management": return "/accounts/manage";
    case "announcement-management": return "/announcements/manage";
    case "my-work": return "/people/work";
    case "leave": return route.section === "approvals" ? "/people/leave/approvals" : "/people/leave";
    case "attendance-management": return route.requestId ? `/attendance/manage/${encodeURIComponent(route.requestId)}` : "/attendance/manage";
    case "leave-management": return "/leave/manage";
    case "personal-settings": return `/personal-settings/${route.section}`;
  }
}

export function workspaceRouteUrl(route: WorkspaceRoute, location: Pick<Location, "search"> = window.location): string {
  const search = new URLSearchParams(location.search);
  search.delete("task");
  const suffix = search.toString();
  return `${workspaceRoutePath(route)}${suffix ? `?${suffix}` : ""}`;
}
